import type { SelectEventArgs } from '@syncfusion/ej2-react-schedule'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { TIME_FORMAT } from 'common/constants/timeFormat'
import { add, differenceInMinutes, format, isAfter } from 'date-fns'
import dayjs from 'dayjs'

import {
  buildScheduleUpdatePayload,
  isWithinAvailableSlots,
  snapToFiveMinutes,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/helpers'
import { mapSchedulesToEvents } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/mapSchedulesToEvents'
import { revertScheduleDomChange } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/safeRefreshScheduleEvents'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { useCallback, useMemo, useState } from 'react'

type PreparedScheduleChange = {
  adjustedData: any
  resourceId: string
}

type UseScheduleCalendarResponse = {
  awaitConfirmationEvent: any
  setAwaitConfirmationEvent: (event: any) => void
  prepareScheduleChange: (data: any) => PreparedScheduleChange | null
  commitScheduleChange: (prepared: PreparedScheduleChange, event: any) => Promise<void>
  handleScheduleChange: (data: any) => Promise<void>
  proceedToUpdateEvents: () => Promise<void>
  events: IPatientSchedule[]
  handleSelect: (props: any) => void
}

type UseScheduleCalendarProps = {
  dayInterval: { min: string; max: string }
  disabledEverything?: boolean
  weekDays?: Record<
    string,
    {
      time_slots: {
        id: number
        start_time: string
        end_time: string
      }[]
    }
  >
  formattedDate: string
  schedules: IPatientSchedule[]
  timeZone: string
  scheduleObj: any
  prepopulateTimeOnClick: number
  handleUpdateEvents: (adjustedData: any, event: any, newDate?: string) => Promise<boolean>
  createCustomAppointment?: (start: Date, end: Date, resourceId: string) => void
}

export const useScheduleWeekCalendar = ({
  dayInterval,
  disabledEverything = false,
  weekDays = {},
  schedules,
  timeZone,
  scheduleObj,
  prepopulateTimeOnClick,
  createCustomAppointment,
  handleUpdateEvents,
}: UseScheduleCalendarProps): UseScheduleCalendarResponse => {
  const [awaitConfirmationEvent, setAwaitConfirmationEvent] = useState<any>(null)

  const { setNotification } = useNotification()

  const shouldProceedToUpdate = ({
    resourceId,
    start,
    end,
  }: {
    resourceId: string
    start: any
    end: any
  }) => {
    const dayData = weekDays[resourceId]

    if (!dayData || !dayData.time_slots.length) {
      setNotification({
        title: 'Update schedule error',
        description: 'No available time slots for this date.',
        type: ALERT_CONSTANTS.ERROR,
      })
      return false
    }

    const isAvailable = isWithinAvailableSlots({
      dateKey: resourceId,
      slots: dayData.time_slots,
      start,
      end,
    })

    if (!isAvailable) {
      setNotification({
        title: 'Update schedule error',
        description: 'Appointment must be within available time slots.',
        type: ALERT_CONSTANTS.ERROR,
      })
      return false
    }

    return true
  }

  const revertScheduleChange = useCallback(() => {
    revertScheduleDomChange(scheduleObj?.current)
  }, [scheduleObj])

  const prepareScheduleChange = (data: IPatientSchedule): PreparedScheduleChange | null => {
    if (disabledEverything) {
      return null
    }

    const start = snapToFiveMinutes(new Date(data.start_time))
    let end = snapToFiveMinutes(new Date(data.end_time))

    const durationInMinutes = differenceInMinutes(end, start)
    if (durationInMinutes < 5) {
      end = dayjs(end).add(5, 'minutes').toDate()
    }

    const resourceId = dayjs(start).format('YYYY-MM-DD')

    const shouldUpdateEvent = shouldProceedToUpdate({
      resourceId,
      start,
      end,
    })

    if (!shouldUpdateEvent) {
      return null
    }

    const adjustedData = buildScheduleUpdatePayload(data, {
      room: typeof data.room === 'string' ? data.room : (data.room as Record<string, any>)?.code,
      start_time: format(start, TIME_FORMAT.TIME_PICKER_HH_SS),
      end_time: format(end, TIME_FORMAT.TIME_PICKER_HH_SS),
    })

    return { adjustedData, resourceId }
  }

  const commitScheduleChange = async (
    { adjustedData, resourceId }: PreparedScheduleChange,
    event: IPatientSchedule,
  ): Promise<void> => {
    try {
      await handleUpdateEvents(adjustedData, event, resourceId)
    } catch {
      revertScheduleChange()
    }
  }

  const handleScheduleChange = async (data: IPatientSchedule): Promise<void> => {
    const prepared = prepareScheduleChange(data)

    if (!prepared) {
      revertScheduleChange()
      return
    }

    await commitScheduleChange(prepared, data)
  }

  const proceedToUpdateEvents = useCallback(async () => {
    if (!awaitConfirmationEvent) {
      return
    }

    const { start, end, event } = awaitConfirmationEvent
    const adjustedData = buildScheduleUpdatePayload(event, {
      room: null,
      start_time: format(start, TIME_FORMAT.TIME_PICKER_HH_SS),
      end_time: format(end, TIME_FORMAT.TIME_PICKER_HH_SS),
    })

    const successUpdate = await handleUpdateEvents(adjustedData, event)
    if (!successUpdate) {
      return
    }

    scheduleObj?.current?.saveEvent({ ...event })
    setAwaitConfirmationEvent(null)
  }, [awaitConfirmationEvent, handleUpdateEvents])

  // Week view uses the event date as the resource/grouping key.
  const eventsToShow = useMemo(
    () => mapSchedulesToEvents(schedules, timeZone, (event) => event.date),
    [schedules, timeZone],
  )

  const handleSelect = useCallback(
    (props: SelectEventArgs) => {
      if (disabledEverything) {
        return
      }

      // In Week view, selection data might be different; keep the permissive lookup below
      // until the exact cell-selection shape is verified at runtime.
      const cellData = (props.data || props) as Record<string, any>
      const startTime = cellData.startTime || cellData.start_time || cellData.StartTime
      const endTime = cellData.endTime || cellData.end_time || cellData.EndTime

      if (!startTime || !endTime) {
        return
      }

      const start_time = new Date(startTime)
      let end_time = new Date(endTime)

      // Get the date from the start_time for week view
      const dateKey = dayjs(start_time).format('YYYY-MM-DD')

      const diffInMinutes = differenceInMinutes(end_time, start_time)
      const prepPopulateTime = prepopulateTimeOnClick
      const updatedEnd = add(start_time, { minutes: prepPopulateTime })

      // Get available time slots for this date
      const dayData = weekDays[dateKey]
      const timeSlots = dayData?.time_slots || []

      // Find the next time slot end that would limit our appointment
      const nextSlotEnd = timeSlots.find((slot: any) => {
        const slotEnd = new Date(`${dateKey} ${slot.end_time}`)
        return isAfter(slotEnd, start_time) && isAfter(updatedEnd, slotEnd)
      })?.end_time

      const endDayDate = new Date(`${dateKey} ${dayInterval.max}`)

      const dateToCheck = nextSlotEnd ? new Date(`${dateKey} ${nextSlotEnd}`) : endDayDate

      if (diffInMinutes < prepPopulateTime && !isAfter(updatedEnd, dateToCheck)) {
        end_time = updatedEnd
      } else if (diffInMinutes < prepPopulateTime && isAfter(updatedEnd, dateToCheck)) {
        end_time = dateToCheck
      }

      createCustomAppointment(start_time, end_time, dateKey)
    },
    [disabledEverything, prepopulateTimeOnClick, weekDays, dayInterval, createCustomAppointment],
  )

  return {
    prepareScheduleChange,
    commitScheduleChange,
    handleScheduleChange,
    events: eventsToShow,
    awaitConfirmationEvent,
    setAwaitConfirmationEvent,
    proceedToUpdateEvents,
    handleSelect,
  }
}
